import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Alprazolam() {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [cartCount, setCartCount] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState('100pills');

  useEffect(() => {
    const updateCartCount = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
        setCartCount(count);
      }
    };
    
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    return () => window.removeEventListener('storage', updateCartCount);
  }, []);

  const packages = [
    { pills: 100, price: 125.99 },
    { pills: 200, price: 225.99 },
    { pills: 300, price: 325.99 }
  ];

  const product = {
    id: 12,
    name: 'Alprazolam 0.5mg',
    category: 'Anxiety',
    description: 'Alprazolam is a benzodiazepine medication used to treat anxiety disorders and panic disorder. It works by enhancing the effects of GABA in the brain.',
    activeIngredient: 'Alprazolam 0.5mg',
    manufacturer: 'Pfizer Inc.',
    image: 'https://public.readdy.ai/ai/img_res/24594714333c9921b980351433563262.jpg',
    rating: 4.6,
    reviews: 342
  };

  const addToCart = () => {
    const savedCart = localStorage.getItem('cart');
    const cart = savedCart ? JSON.parse(savedCart) : [];
    
    const cartItem = {
      id: `${product.id}-${packages[selectedPackage].pills}`,
      name: `${product.name} - ${packages[selectedPackage].pills} Pills`,
      price: packages[selectedPackage].price,
      quantity: quantity,
      image: product.image
    };
    
    const existingItem = cart.find((item: any) => item.id === cartItem.id);
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      cart.push(cartItem);
    }
    
    localStorage.setItem('cart', JSON.stringify(cart));
    setCartCount(cart.reduce((sum: number, item: any) => sum + item.quantity, 0));
    window.dispatchEvent(new Event('storage'));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>MediCare</Link>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              <a href="/#categories" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Categories</a>
              <Link to="/all-products" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Products</Link>
              <Link to="/prescription" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Prescription</Link>
              <a href="/#contact" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/all-products" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                <i className="ri-search-line text-xl"></i>
              </Link>
              <Link to="/cart" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer relative">
                <i className="ri-shopping-cart-line text-xl"></i>
                <span className="absolute -top-1 -right-1 bg-teal-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>
              </Link>
              <Link to="/auth" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                <i className="ri-user-line text-xl"></i>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Product Detail */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Product Image */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <img src={product.image} alt={product.name} className="w-full h-auto object-cover object-top rounded-xl" />
          </div>

          {/* Product Info */}
          <div>
            <div className="mb-4">
              <span className="inline-block bg-teal-100 text-teal-600 px-4 py-1 rounded-full text-sm font-semibold">{product.category}</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
            
            <div className="flex items-center mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i
                    key={i}
                    className={`${
                      i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'
                    } text-yellow-400 text-lg`}
                  ></i>
                ))}
              </div>
              <span className="ml-2 text-gray-600">({product.rating}) - {product.reviews} reviews</span>
            </div>

            <p className="text-gray-700 mb-6 leading-relaxed">{product.description}</p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <div className="flex items-start">
                <i className="ri-information-line text-blue-500 text-xl mr-3 mt-1"></i>
                <p className="text-sm text-blue-700"><strong>Prescription Required:</strong> This medication requires a valid prescription from a licensed healthcare provider.</p>
              </div>
            </div>

            {/* Package Selection */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Select Package</h3>
              <div className="grid grid-cols-3 gap-3">
                {packages.map((pkg, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedPackage(index)}
                    className={`p-4 rounded-lg border-2 transition-all whitespace-nowrap cursor-pointer ${
                      selectedPackage === index
                        ? 'border-teal-600 bg-teal-50'
                        : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="text-center">
                      <p className="font-bold text-gray-900">{pkg.pills} Pills</p>
                      <p className="text-teal-600 font-semibold">${pkg.price}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Quantity</h3>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-subtract-line"></i>
                </button>
                <span className="text-xl font-semibold w-12 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
            </div>

            {/* Price */}
            <div className="mb-6">
              <div className="flex items-baseline">
                <span className="text-4xl font-bold text-gray-900">${(packages[selectedPackage].price * quantity).toFixed(2)}</span>
                <span className="ml-2 text-gray-500">Total</span>
              </div>
            </div>

            {/* Add to Cart */}
            <button
              onClick={addToCart}
              className="w-full bg-teal-600 text-white px-8 py-4 rounded-lg hover:bg-teal-700 transition-colors text-lg font-semibold whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-cart-line mr-2"></i>
              Add to Cart
            </button>

            {/* Product Details */}
            <div className="mt-8 border-t border-gray-200 pt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Details</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                  <span className="text-gray-700"><strong>Active Ingredient:</strong> {product.activeIngredient}</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                  <span className="text-gray-700"><strong>Manufacturer:</strong> {product.manufacturer}</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                  <span className="text-gray-700"><strong>Form:</strong> Oral Tablet</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                  <span className="text-gray-700"><strong>Storage:</strong> Store at room temperature away from moisture and heat</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: '"Pacifico", serif' }}>MediCare</h3>
              <p className="text-gray-400 mb-4">Your trusted partner in healthcare, delivering quality medicines to your doorstep.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-teal-400 transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/all-products" className="hover:text-teal-400 transition-colors cursor-pointer">All Products</Link></li>
                <li><Link to="/prescription" className="hover:text-teal-400 transition-colors cursor-pointer">Prescription</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Categories</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">Weight Loss</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">Antibiotics</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">Pain Relief</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-phone-line mt-1 mr-2"></i>
                  <span>+1 (929) 268-8770</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-mail-line mt-1 mr-2"></i>
                  <span>sam4104t@gmail.com</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">&copy; 2024 MediCare. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-teal-400 transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
